import { apiClient } from './client';
import type {
  AddDepartmentGroupsResult,
  AdditionalSupervisors,
  OnsiteLiveAdminStatus,
  OnsiteLiveBranchSet,
  OnsiteLiveBranchSetWrite,
  OnsiteLiveLookups,
  OnsiteLiveMemberships,
  OnsiteLiveSecurityGroup,
  OnsiteLiveSecurityGroupWrite,
  OnsiteLiveSubscriptionGroup,
  OnsiteLiveSubscriptionGroupWrite,
  OnsiteLiveUser,
  OnsiteLiveUserListItem,
  OnsiteLiveUserWrite,
  MailRecipient,
  PersonDepartmentGroups,
} from '@/types/oscar';

/**
 * Typed wrappers over `/api/onsitelive-admin/*` (migrations 141/142; plan §5)
 * — the screens that replace OnBudget's "Onsite Admin" ribbon. Reads are admin
 * + helpdesk; every write is admin-only, and refused (409) until the cutover.
 */

const base = '/api/onsitelive-admin';

export function getOnsiteLiveAdminStatus(signal?: AbortSignal): Promise<OnsiteLiveAdminStatus> {
  return apiClient.get<OnsiteLiveAdminStatus>(`${base}/status`, signal);
}

export function getOnsiteLiveLookups(signal?: AbortSignal): Promise<OnsiteLiveLookups> {
  return apiClient.get<OnsiteLiveLookups>(`${base}/lookups`, signal);
}

/**
 * Up to 500 users, by username. Search matches username, full name or email.
 * personId = the user(s) linked to one Oscar person, active or not.
 * picker = the manager pickers: active users of active, not-terminated people, by name.
 */
export function listOnsiteLiveUsers(
  params: { search?: string; includeInactive?: boolean; personId?: number; picker?: boolean },
  signal?: AbortSignal,
): Promise<OnsiteLiveUserListItem[]> {
  const qs = new URLSearchParams();
  if (params.search?.trim()) qs.set('search', params.search.trim());
  if (params.includeInactive) qs.set('includeInactive', 'true');
  if (params.personId) qs.set('personId', String(params.personId));
  if (params.picker) qs.set('picker', 'true');
  const q = qs.toString();
  return apiClient.get<OnsiteLiveUserListItem[]>(`${base}/users${q ? `?${q}` : ''}`, signal);
}

/** The email divert lookup: Entra mailboxes, shared mailboxes and mail-enabled groups (never guests or leavers). */
export function searchMailRecipients(search: string, signal?: AbortSignal): Promise<MailRecipient[]> {
  return apiClient.get<MailRecipient[]>(`${base}/mail-recipients?search=${encodeURIComponent(search.trim())}`, signal);
}

export function getOnsiteLiveUser(id: number, signal?: AbortSignal): Promise<OnsiteLiveUser> {
  return apiClient.get<OnsiteLiveUser>(`${base}/users/${id}`, signal);
}

export function createOnsiteLiveUser(body: OnsiteLiveUserWrite): Promise<OnsiteLiveUser> {
  return apiClient.post<OnsiteLiveUser>(`${base}/users`, body);
}

export function updateOnsiteLiveUser(id: number, body: OnsiteLiveUserWrite): Promise<OnsiteLiveUser> {
  return apiClient.patch<OnsiteLiveUser>(`${base}/users/${id}`, body);
}

/** A user's additional supervisors (OnFlow "Additional" PO approvers). */
export function getAdditionalSupervisors(id: number, signal?: AbortSignal): Promise<AdditionalSupervisors> {
  return apiClient.get<AdditionalSupervisors>(`${base}/users/${id}/additional-supervisors`, signal);
}

/** REPLACES the user's additional supervisors (refused until the subscriptions cutover). */
export function setAdditionalSupervisors(id: number, supervisorUserIds: number[]): Promise<AdditionalSupervisors> {
  return apiClient.put<AdditionalSupervisors>(`${base}/users/${id}/additional-supervisors`, { SupervisorUserIds: supervisorUserIds });
}

/** The account's department, the OnsiteLive groups it gets, and which the account is in. */
export function getDepartmentGroups(id: number, signal?: AbortSignal): Promise<PersonDepartmentGroups> {
  return apiClient.get<PersonDepartmentGroups>(`${base}/users/${id}/department-groups`, signal);
}

/** Adds the department's missing OnsiteLive groups — never removes one (admin). */
export function addDepartmentGroups(id: number): Promise<AddDepartmentGroupsResult> {
  return apiClient.post<AddDepartmentGroupsResult>(`${base}/users/${id}/department-groups`);
}

/** Each list REPLACES the user's memberships of that kind. */
export function setOnsiteLiveMemberships(id: number, body: OnsiteLiveMemberships): Promise<OnsiteLiveMemberships> {
  return apiClient.put<OnsiteLiveMemberships>(`${base}/users/${id}/memberships`, body);
}

export function saveOnsiteLiveSecurityGroup(id: number | null, body: OnsiteLiveSecurityGroupWrite): Promise<OnsiteLiveSecurityGroup> {
  return id === null
    ? apiClient.post<OnsiteLiveSecurityGroup>(`${base}/security-groups`, body)
    : apiClient.patch<OnsiteLiveSecurityGroup>(`${base}/security-groups/${id}`, body);
}

export function saveOnsiteLiveSubscriptionGroup(
  id: number | null,
  body: OnsiteLiveSubscriptionGroupWrite,
): Promise<OnsiteLiveSubscriptionGroup> {
  return id === null
    ? apiClient.post<OnsiteLiveSubscriptionGroup>(`${base}/subscription-groups`, body)
    : apiClient.patch<OnsiteLiveSubscriptionGroup>(`${base}/subscription-groups/${id}`, body);
}

export function saveOnsiteLiveBranchSet(id: number | null, body: OnsiteLiveBranchSetWrite): Promise<OnsiteLiveBranchSet> {
  return id === null
    ? apiClient.post<OnsiteLiveBranchSet>(`${base}/branch-sets`, body)
    : apiClient.patch<OnsiteLiveBranchSet>(`${base}/branch-sets/${id}`, body);
}
